import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Grid,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useColorModeValue,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Icon,
  VStack,
  HStack,
  Divider,
  Badge,
  SimpleGrid,
  Progress,
  Button,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Switch,
  Tooltip,
  Tag,
} from "@chakra-ui/react";
import {
  BsPeople,
  BsPersonCheck,
  BsCalendarCheck,
  BsBriefcase,
  BsGraphUp,
  BsClipboardCheck,
  BsPersonPlus,
  BsArrowUp,
  BsArrowDown,
  BsSliders,
} from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import api from "../services/api.js";

// ── Card registry ────────────────────────────────────────────────────────────

const STAT_CARDS = [
  { id: "stat_total_employees", label: "Total Employees" },
  { id: "stat_new_hires", label: "New Hires This Month" },
  { id: "stat_attendance_rate", label: "Attendance Rate" },
  { id: "stat_open_positions", label: "Open Positions" },
];

const SECTION_CARDS = [
  { id: "headcount_by_dept", label: "Headcount by Department" },
  { id: "headcount_by_location", label: "Headcount by Location" },
  {
    id: "recruitment_pipeline",
    label: "Recruitment Pipeline",
    fullWidth: true,
  },
  { id: "attendance_summary", label: "Attendance Summary" },
  { id: "quick_actions", label: "Quick Actions" },
  { id: "procurement_requests", label: "Procurement Requests" },
  { id: "support_tickets", label: "Support Tickets" },
];

const KPI_CARD_STYLES = {
  stat_total_employees: {
    accent: "gray.700",
    badgeLight: "gray.100",
    badgeDark: "whiteAlpha.200",
  },
  stat_new_hires: {
    accent: "teal.600",
    badgeLight: "green.50",
    badgeDark: "green.900",
  },
  stat_attendance_rate: {
    accent: "blue.600",
    badgeLight: "blue.50",
    badgeDark: "blue.900",
  },
  stat_open_positions: {
    accent: "purple.600",
    badgeLight: "purple.50",
    badgeDark: "purple.900",
  },
};

const buildDefaultConfig = () => [
  ...STAT_CARDS.map((c, i) => ({ id: c.id, visible: true, order: i })),
  ...SECTION_CARDS.map((c, i) => ({ id: c.id, visible: true, order: i })),
];

const StatCard = ({
  title,
  value,
  icon,
  helpText,
  trend,
  isLoading,
  styleConfig,
}) => {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const iconBg = useColorModeValue(
    styleConfig?.badgeLight || "teal.50",
    styleConfig?.badgeDark || "teal.900",
  );
  const labelColor = useColorModeValue("gray.600", "gray.300");
  const numberColor = useColorModeValue("gray.900", "whiteAlpha.900");
  const hoverBorder = useColorModeValue("gray.300", "gray.600");

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
      _hover={{
        borderColor: hoverBorder,
        boxShadow: "md",
      }}
      transition="all 0.2s ease"
    >
      {isLoading ? (
        <Center h="100px">
          <Spinner size="md" />
        </Center>
      ) : (
        <Stat>
          <HStack justify="space-between" mb={3}>
            <StatLabel
              fontSize="xs"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color={labelColor}
            >
              {title}
            </StatLabel>
            <Center w="40px" h="40px" borderRadius="lg" bg={iconBg}>
              <Icon
                as={icon}
                boxSize={5}
                color={styleConfig?.accent || "teal.500"}
              />
            </Center>
          </HStack>
          <StatNumber fontSize="3xl" fontWeight="extrabold" color={numberColor}>
            {value}
          </StatNumber>
          {helpText && (
            <StatHelpText mb={0} mt={1} color={labelColor}>
              {trend && <StatArrow type={trend} />}
              {helpText}
            </StatHelpText>
          )}
        </Stat>
      )}
    </Box>
  );
};

const SectionCard = ({ title, children, isLoading }) => {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headingColor = useColorModeValue("gray.800", "gray.100");
  const dividerColor = useColorModeValue("gray.100", "whiteAlpha.200");

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
      h="full"
    >
      <Heading size="sm" mb={3} color={headingColor} fontWeight="semibold">
        {title}
      </Heading>
      <Divider mb={4} borderColor={dividerColor} />
      {isLoading ? (
        <Center h="200px">
          <Spinner size="lg" />
        </Center>
      ) : (
        children
      )}
    </Box>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [headcountData, setHeadcountData] = useState(null);
  const [recruitmentData, setRecruitmentData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [operationsKPIs, setOperationsKPIs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Card config state
  const [cardConfig, setCardConfig] = useState(buildDefaultConfig());
  const [localConfig, setLocalConfig] = useState(buildDefaultConfig());
  const [configSaving, setConfigSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [dashRes, headRes, recRes, attRes, opsRes, cfgRes] =
        await Promise.all([
          api.get("/api/analytics/dashboard"),
          api.get("/api/analytics/headcount"),
          api.get("/api/analytics/recruitment"),
          api.get("/api/analytics/attendance"),
          api.get("/api/analytics/operations-kpis"),
          api.get("/api/analytics/dashboard-config"),
        ]);

      setDashboardData(dashRes.data.data);
      setHeadcountData(headRes.data.data);
      setRecruitmentData(recRes.data.data);
      setAttendanceData(attRes.data.data);
      setOperationsKPIs(opsRes.data.data);

      const saved = cfgRes.data.data?.cards;
      if (saved?.length) {
        setCardConfig(saved);
        setLocalConfig(saved);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.response?.data?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // ── Customize drawer handlers ──────────────────────────────────────────────

  const openCustomize = () => {
    setLocalConfig([...cardConfig]);
    onOpen();
  };

  const toggleVisible = (id) => {
    setLocalConfig((prev) =>
      prev.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c)),
    );
  };

  const moveSection = (id, direction) => {
    setLocalConfig((prev) => {
      const sections = prev
        .filter((c) => SECTION_CARDS.some((s) => s.id === c.id))
        .slice()
        .sort((a, b) => a.order - b.order);

      const idx = sections.findIndex((c) => c.id === id);
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= sections.length) return prev;

      const newSections = [...sections];
      const tmp = newSections[idx].order;
      newSections[idx] = {
        ...newSections[idx],
        order: newSections[swapIdx].order,
      };
      newSections[swapIdx] = { ...newSections[swapIdx], order: tmp };

      return prev.map((c) => {
        const updated = newSections.find((s) => s.id === c.id);
        return updated || c;
      });
    });
  };

  const navigateToProcurement = useCallback(
    (status) => {
      const params = new URLSearchParams();
      if (status) {
        params.set("status", status);
      }
      const search = params.toString();
      navigate(`/procurement/requests${search ? `?${search}` : ""}`);
    },
    [navigate],
  );

  const navigateToTicketing = useCallback(
    (workflowState) => {
      const params = new URLSearchParams();
      if (workflowState) {
        params.set("workflowState", workflowState);
      }
      const search = params.toString();
      navigate(`/Ticketing${search ? `?${search}` : ""}`);
    },
    [navigate],
  );

  const saveConfig = async () => {
    try {
      setConfigSaving(true);
      const res = await api.put("/api/analytics/dashboard-config", {
        cards: localConfig,
      });
      const saved = res.data.data?.cards || localConfig;
      setCardConfig(saved);
      onClose();
    } catch (err) {
      console.error("Error saving dashboard config:", err);
    } finally {
      setConfigSaving(false);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const isVisible = useCallback(
    (id) => {
      const entry = cardConfig.find((c) => c.id === id);
      return entry ? entry.visible : true;
    },
    [cardConfig],
  );

  const visibleSectionCards = SECTION_CARDS.filter((card) =>
    isVisible(card.id),
  ).sort((a, b) => {
    const oa = cardConfig.find((c) => c.id === a.id)?.order ?? 99;
    const ob = cardConfig.find((c) => c.id === b.id)?.order ?? 99;
    return oa - ob;
  });

  const visibleStatIds = STAT_CARDS.filter((c) => isVisible(c.id)).map(
    (c) => c.id,
  );

  // ── Stat card renders ──────────────────────────────────────────────────────

  const renderStatCard = (id) => {
    const styleConfig = KPI_CARD_STYLES[id];

    switch (id) {
      case "stat_total_employees":
        return (
          <StatCard
            key={id}
            title="Total Employees"
            value={dashboardData?.headcount?.total || 0}
            icon={BsPeople}
            helpText={`${dashboardData?.headcount?.active || 0} active`}
            isLoading={loading}
            styleConfig={styleConfig}
          />
        );
      case "stat_new_hires":
        return (
          <StatCard
            key={id}
            title="New Hires This Month"
            value={dashboardData?.recruitment?.newHires || 0}
            icon={BsPersonPlus}
            trend="increase"
            helpText="Compared to last month"
            isLoading={loading}
            styleConfig={styleConfig}
          />
        );
      case "stat_attendance_rate":
        return (
          <StatCard
            key={id}
            title="Attendance Rate"
            value={`${dashboardData?.attendance?.rate || 0}%`}
            icon={BsCalendarCheck}
            helpText={`${dashboardData?.attendance?.present || 0} present today`}
            isLoading={loading}
            styleConfig={styleConfig}
          />
        );
      case "stat_open_positions":
        return (
          <StatCard
            key={id}
            title="Open Positions"
            value={dashboardData?.recruitment?.openPositions || 0}
            icon={BsBriefcase}
            helpText={`${dashboardData?.recruitment?.activeApplications || 0} active applications`}
            isLoading={loading}
            styleConfig={styleConfig}
          />
        );
      default:
        return null;
    }
  };

  // ── Section card renders ───────────────────────────────────────────────────

  const renderSectionCard = (card) => {
    switch (card.id) {
      case "headcount_by_dept":
        return (
          <SectionCard
            key={card.id}
            title="Headcount by Department"
            isLoading={loading}
          >
            <VStack spacing={3} align="stretch">
              {headcountData?.byDepartment?.slice(0, 5).map((dept, idx) => (
                <HStack key={idx} justify="space-between">
                  <Text fontWeight="medium">{dept._id || "Unassigned"}</Text>
                  <Badge colorScheme="teal" fontSize="md" px={3} py={1}>
                    {dept.count}
                  </Badge>
                </HStack>
              ))}
              {!headcountData?.byDepartment?.length && (
                <Text color="gray.500" textAlign="center" py={8}>
                  No department data available
                </Text>
              )}
            </VStack>
          </SectionCard>
        );

      case "headcount_by_location":
        return (
          <SectionCard
            key={card.id}
            title="Headcount by Location"
            isLoading={loading}
          >
            <VStack spacing={3} align="stretch">
              {headcountData?.byLocation?.slice(0, 5).map((loc, idx) => (
                <HStack key={idx} justify="space-between">
                  <Text fontWeight="medium">{loc._id || "Unassigned"}</Text>
                  <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                    {loc.count}
                  </Badge>
                </HStack>
              ))}
              {!headcountData?.byLocation?.length && (
                <Text color="gray.500" textAlign="center" py={8}>
                  No location data available
                </Text>
              )}
            </VStack>
          </SectionCard>
        );

      case "recruitment_pipeline":
        return (
          <Box key={card.id} gridColumn="1 / -1">
            <SectionCard title="Recruitment Pipeline" isLoading={loading}>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={6}
              >
                <Box
                  textAlign="center"
                  p={4}
                  borderRadius="lg"
                  bg={metricTileBg}
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                >
                  <Icon as={BsBriefcase} boxSize={8} color="blue.500" mb={2} />
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {recruitmentData?.activeJobs || 0}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Active Job Postings
                  </Text>
                </Box>
                <Box
                  textAlign="center"
                  p={4}
                  borderRadius="lg"
                  bg={metricTileBg}
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                >
                  <Icon
                    as={BsPersonCheck}
                    boxSize={8}
                    color="teal.500"
                    mb={2}
                  />
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {recruitmentData?.pipeline?.total || 0}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Total Applications
                  </Text>
                </Box>
                <Box
                  textAlign="center"
                  p={4}
                  borderRadius="lg"
                  bg={metricTileBg}
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                >
                  <Icon as={BsGraphUp} boxSize={8} color="green.500" mb={2} />
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {recruitmentData?.pipeline?.conversionRate || 0}%
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Conversion Rate
                  </Text>
                </Box>
              </Grid>
              <Divider my={4} />
              <VStack spacing={3} align="stretch">
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Applications by Stage
                </Text>
                {recruitmentData?.pipeline?.byStage?.map((stage, idx) => (
                  <HStack key={idx} justify="space-between">
                    <Text textTransform="capitalize">
                      {stage._id?.replace(/_/g, " ")}
                    </Text>
                    <Badge colorScheme="purple" fontSize="sm" px={2}>
                      {stage.count}
                    </Badge>
                  </HStack>
                ))}
              </VStack>
            </SectionCard>
          </Box>
        );

      case "attendance_summary":
        return (
          <SectionCard
            key={card.id}
            title="Attendance Summary"
            isLoading={loading}
          >
            <VStack spacing={4} align="stretch">
              <Box
                p={4}
                bg={metricTileBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={metricTileBorder}
              >
                <HStack justify="space-between">
                  <Text fontWeight="semibold">Overall Attendance</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">
                    {attendanceData?.summary?.attendanceRate || 0}%
                  </Text>
                </HStack>
              </Box>
              <SimpleGrid columns={2} spacing={4}>
                <Box
                  textAlign="center"
                  p={3}
                  bg={metricTileBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => navigate("/attendance")}
                >
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {attendanceData?.summary?.totalPresent || 0}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Present
                  </Text>
                </Box>
                <Box
                  textAlign="center"
                  p={3}
                  bg={metricTileBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => navigate("/attendance")}
                >
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {attendanceData?.summary?.expected || 0}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Expected
                  </Text>
                </Box>
              </SimpleGrid>
            </VStack>
          </SectionCard>
        );

      case "quick_actions":
        return (
          <SectionCard key={card.id} title="Quick Actions" isLoading={false}>
            <VStack spacing={3} align="stretch">
              <Box
                p={4}
                borderRadius="lg"
                borderWidth="1px"
                borderColor="gray.200"
                _hover={{ bg: "gray.50", cursor: "pointer" }}
              >
                <HStack>
                  <Icon as={BsClipboardCheck} color="teal.500" />
                  <Text fontWeight="medium">
                    Pending Leaves: {dashboardData?.leaves?.pending || 0}
                  </Text>
                </HStack>
              </Box>
              <Box
                p={4}
                borderRadius="lg"
                borderWidth="1px"
                borderColor="gray.200"
                _hover={{ bg: "gray.50", cursor: "pointer" }}
              >
                <HStack>
                  <Icon as={BsBriefcase} color="blue.500" />
                  <Text fontWeight="medium">
                    Active Recruitments:{" "}
                    {dashboardData?.recruitment?.activeApplications || 0}
                  </Text>
                </HStack>
              </Box>
              <Box
                p={4}
                borderRadius="lg"
                borderWidth="1px"
                borderColor="gray.200"
                _hover={{ bg: "gray.50", cursor: "pointer" }}
              >
                <HStack>
                  <Icon as={BsPeople} color="purple.500" />
                  <Text fontWeight="medium">
                    Active Employees: {dashboardData?.headcount?.active || 0}
                  </Text>
                </HStack>
              </Box>
            </VStack>
          </SectionCard>
        );

      case "procurement_requests":
        return (
          <SectionCard
            key={card.id}
            title="Procurement Requests"
            isLoading={loading}
          >
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={2} spacing={3}>
                {/* TOTAL */}
                <Box
                  textAlign="center"
                  p={3}
                  bg={metricTileBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => navigateToProcurement()}
                >
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {operationsKPIs?.procurement?.total || 0}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Total
                  </Text>
                </Box>

                {/* PENDING REVIEW */}
                <Box
                  textAlign="center"
                  p={3}
                  bg={metricTileBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => navigateToProcurement("SUBMITTED")}
                >
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {operationsKPIs?.procurement?.byStatus?.submitted || 0}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Pending Review
                  </Text>
                </Box>

                {/* APPROVED */}
                <Box
                  textAlign="center"
                  p={3}
                  bg={metricTileBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => navigateToProcurement("APPROVED")}
                >
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {operationsKPIs?.procurement?.byStatus?.approved || 0}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Approved
                  </Text>
                </Box>

                {/* REJECTED */}
                <Box
                  textAlign="center"
                  p={3}
                  bg={metricTileBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => navigateToProcurement("REJECTED")}
                >
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {operationsKPIs?.procurement?.byStatus?.rejected || 0}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Rejected
                  </Text>
                </Box>
              </SimpleGrid>

              <Divider />

              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Top Categories
              </Text>

              {operationsKPIs?.procurement?.byCategory?.length ? (
                operationsKPIs.procurement.byCategory.map((cat, idx) => (
                  <HStack key={idx} justify="space-between">
                    <Text fontSize="sm">{cat._id || "Uncategorised"}</Text>
                    <Badge colorScheme="orange" px={2}>
                      {cat.count}
                    </Badge>
                  </HStack>
                ))
              ) : (
                <Text color="gray.500" textAlign="center" fontSize="sm">
                  No procurement data
                </Text>
              )}
            </VStack>
          </SectionCard>
        );
      case "support_tickets":
        return (
          <SectionCard
            key={card.id}
            title="Support Tickets"
            isLoading={loading}
          >
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={2} spacing={3}>
                <Box
                  textAlign="center"
                  p={3}
                  bg={metricTileBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => navigateToTicketing("open")}
                >
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {operationsKPIs?.ticketing?.byStatus?.open || 0}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Open
                  </Text>
                </Box>
                <Box
                  textAlign="center"
                  p={3}
                  bg={metricTileBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => navigateToTicketing("in_progress")}
                >
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {operationsKPIs?.ticketing?.byStatus?.inProgress || 0}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    In Progress
                  </Text>
                </Box>
                <Box
                  textAlign="center"
                  p={3}
                  bg={metricTileBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => navigateToTicketing("on_hold")}
                >
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {operationsKPIs?.ticketing?.byStatus?.onHold || 0}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    On Hold
                  </Text>
                </Box>
                <Box
                  textAlign="center"
                  p={3}
                  bg={metricTileBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={metricTileBorder}
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => navigateToTicketing("resolved")}
                >
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={metricValueColor}
                  >
                    {operationsKPIs?.ticketing?.byStatus?.resolved || 0}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Resolved
                  </Text>
                </Box>
              </SimpleGrid>
              <Divider />
              <HStack justify="space-between">
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Resolution Rate
                </Text>
                <Text fontWeight="bold" color="teal.600">
                  {operationsKPIs?.ticketing?.resolutionRate || 0}%
                </Text>
              </HStack>
              <Progress
                value={operationsKPIs?.ticketing?.resolutionRate || 0}
                colorScheme="teal"
                borderRadius="full"
                size="sm"
              />
              <Text fontWeight="semibold" fontSize="sm" color="gray.600" pt={1}>
                By Priority
              </Text>
              {operationsKPIs?.ticketing?.byPriority?.length ? (
                operationsKPIs.ticketing.byPriority.map((p, idx) => (
                  <HStack key={idx} justify="space-between">
                    <Text fontSize="sm" textTransform="capitalize">
                      {p._id || "None"}
                    </Text>
                    <Badge
                      colorScheme={
                        p._id === "Urgent"
                          ? "red"
                          : p._id === "High"
                            ? "orange"
                            : p._id === "Medium"
                              ? "yellow"
                              : "gray"
                      }
                      px={2}
                    >
                      {p.count}
                    </Badge>
                  </HStack>
                ))
              ) : (
                <Text color="gray.500" textAlign="center" fontSize="sm">
                  No ticket data
                </Text>
              )}
            </VStack>
          </SectionCard>
        );

      default:
        return null;
    }
  };

  // ── Customize drawer ───────────────────────────────────────────────────────

  const localSections = SECTION_CARDS.map((card) => {
    const cfg = localConfig.find((c) => c.id === card.id) || {
      id: card.id,
      visible: true,
      order: 0,
    };
    return { ...card, ...cfg };
  }).sort((a, b) => a.order - b.order);

  const bg = useColorModeValue("gray.50", "gray.900");
  const heroTextSubtle = useColorModeValue("gray.500", "gray.300");
  const metricTileBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const metricTileBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const metricValueColor = useColorModeValue("gray.800", "gray.100");

  if (error) {
    return (
      <Box bg={bg} minH="100vh" py={8}>
        <Container maxW="7xl">
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg={bg} minH="100vh" py={8}>
      <Container maxW="7xl">
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <HStack justify="space-between" align="center">
            <Box>
              <Heading size="lg" lineHeight="1.1">
                Dashboard
              </Heading>
              <Text color={heroTextSubtle} mt={2} fontSize="sm">
                Track workforce health and operational trends.
              </Text>
            </Box>
            <Tooltip label="Customize dashboard cards">
              <Button
                leftIcon={<BsSliders />}
                size="sm"
                variant="outline"
                colorScheme="teal"
                onClick={openCustomize}
              >
                Customize
              </Button>
            </Tooltip>
          </HStack>

          {/* KPI Stat Cards */}
          {visibleStatIds.length > 0 && (
            <SimpleGrid
              columns={{ base: 1, sm: 2, lg: visibleStatIds.length }}
              spacing={6}
            >
              {visibleStatIds.map((id) => renderStatCard(id))}
            </SimpleGrid>
          )}

          {/* Section Cards — dynamic order */}
          <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6}>
            {visibleSectionCards.map((card) => renderSectionCard(card))}
          </Grid>
        </VStack>
      </Container>

      {/* Customize Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <HStack>
              <Icon as={BsSliders} />
              <Text>Customize Dashboard</Text>
            </HStack>
          </DrawerHeader>

          <DrawerBody py={6}>
            <VStack spacing={6} align="stretch">
              {/* KPI Cards */}
              <Box>
                <HStack mb={3}>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.500">
                    KPI CARDS
                  </Text>
                  <Tag size="sm" colorScheme="gray">
                    always at top
                  </Tag>
                </HStack>
                <VStack spacing={2} align="stretch">
                  {STAT_CARDS.map((card) => {
                    const cfg = localConfig.find((c) => c.id === card.id) || {
                      visible: true,
                    };
                    return (
                      <HStack
                        key={card.id}
                        p={3}
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor="gray.200"
                        justify="space-between"
                        bg={cfg.visible ? "white" : "gray.50"}
                        opacity={cfg.visible ? 1 : 0.6}
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color={cfg.visible ? "gray.800" : "gray.400"}
                        >
                          {card.label}
                        </Text>
                        <Switch
                          isChecked={cfg.visible}
                          onChange={() => toggleVisible(card.id)}
                          colorScheme="teal"
                          size="sm"
                        />
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>

              <Divider />

              {/* Section Cards */}
              <Box>
                <Text
                  fontWeight="semibold"
                  fontSize="sm"
                  color="gray.500"
                  mb={3}
                >
                  SECTION CARDS
                </Text>
                <VStack spacing={2} align="stretch">
                  {localSections.map((card, idx) => (
                    <HStack
                      key={card.id}
                      p={3}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="gray.200"
                      justify="space-between"
                      bg={card.visible ? "white" : "gray.50"}
                      opacity={card.visible ? 1 : 0.6}
                    >
                      <VStack spacing={0}>
                        <IconButton
                          icon={<BsArrowUp />}
                          size="xs"
                          variant="ghost"
                          aria-label="Move up"
                          isDisabled={idx === 0}
                          onClick={() => moveSection(card.id, "up")}
                        />
                        <IconButton
                          icon={<BsArrowDown />}
                          size="xs"
                          variant="ghost"
                          aria-label="Move down"
                          isDisabled={idx === localSections.length - 1}
                          onClick={() => moveSection(card.id, "down")}
                        />
                      </VStack>
                      <Text
                        flex={1}
                        fontSize="sm"
                        fontWeight="medium"
                        ml={2}
                        color={card.visible ? "gray.800" : "gray.400"}
                      >
                        {card.label}
                      </Text>
                      <Switch
                        isChecked={card.visible}
                        onChange={() => toggleVisible(card.id)}
                        colorScheme="teal"
                        size="sm"
                      />
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <Button
                colorScheme="teal"
                onClick={saveConfig}
                isLoading={configSaving}
                loadingText="Saving…"
                mt={2}
              >
                Save Layout
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Dashboard;
